import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import type { AccountInfo } from '@azure/msal-browser';
import { Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { decodeJwtPayload } from './jwt-payload.util';
import { isAzureAdEnabled } from './azure-ad-env';
import { AirportCodesStorageService } from '../../shared/services/airport-codes-storage.service';
import type { GetUserDetailsResponse } from '../../shared/services/user-api.service';
import { OverviewApiService } from '../../shared/services/overview-api.service';
import {
  normalizeUserAccessRole,
  resolveUserAccessRole,
  type OptionItem,
  type UserAccessRole
} from '../../shared/models/overview.models';
import { AuthLoggerService } from './auth-logger.service';

const AUTH_KEY = 'spice.auth';
const TOKEN_KEY = 'spice.token';
const REFRESH_TOKEN_KEY = 'spice.refresh_token';
const USER_ID_KEY = 'spice.user_id';
const USER_DATA_KEY = 'spice.user_data';

function pickStringFromRecord(obj: Record<string, unknown>, keys: string[]): string {
  return pickScalarFromRecord(obj, keys);
}

export type { UserAccessRole } from '../../shared/models/overview.models';
export { normalizeUserAccessRole } from '../../shared/models/overview.models';

function pickScalarFromRecord(obj: Record<string, unknown>, keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
}

/** `created_by` / `modified_by`: login and user-details payloads vary (`user_id`, `UserId`, `id`, …). */
function pickUserIdFromProfilePayload(
  merged: Record<string, unknown> | null | undefined,
  envelope: Record<string, unknown>
): string | null {
  const keyOrder = ['user_id', 'UserId', 'userid', 'USER_ID', 'id', 'Id'] as const;
  const tryRec = (rec: Record<string, unknown> | null | undefined): string | null => {
    if (!rec) return null;
    for (const k of keyOrder) {
      const v = rec[k];
      if (v == null || v === '') continue;
      const s = String(v).trim();
      if (s) return s;
    }
    return null;
  };
  return tryRec(merged) ?? tryRec(envelope);
}

/** Merge `data`, nested `data.user`, root `user`, and root-level user fields from login / get_user_details JSON. */
function mergeLoginPayloadToUserBlob(res: Record<string, unknown>): Record<string, unknown> | null {
  const merged: Record<string, unknown> = {};
  const data = res['data'];
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    Object.assign(merged, data as Record<string, unknown>);
    const nestedUser = (data as Record<string, unknown>)['user'];
    if (nestedUser && typeof nestedUser === 'object' && !Array.isArray(nestedUser)) {
      Object.assign(merged, nestedUser as Record<string, unknown>);
    }
  }
  const rootUser = res['user'];
  if (rootUser && typeof rootUser === 'object' && !Array.isArray(rootUser)) {
    Object.assign(merged, rootUser as Record<string, unknown>);
  }
  const rootKeys = [
    'user_id',
    'user_name',
    'username',
    'name',
    'full_name',
    'display_name',
    'email',
    'role',
    'Role',
    'user_role',
    'designation'
  ];
  for (const k of rootKeys) {
    if (res[k] != null && merged[k] == null) {
      merged[k] = res[k];
    }
  }
  return Object.keys(merged).length > 0 ? merged : null;
}

/** Refresh endpoint path from environment. */
function getRefreshPath(): string {
  return (typeof (environment as { refreshApiPath?: string }).refreshApiPath === 'string')
    ? (environment as { refreshApiPath: string }).refreshApiPath
    : '/api/v1/refresh';
}

export interface LoginRequest {
  username: string;
  password: string;
}

/** User object inside login response data. */
export interface LoginUserData {
  user_id?: string;
  user_name?: string;
  /** Some APIs use `username` instead of `user_name`. */
  username?: string;
  role?: string;
  is_active?: boolean;
  created_on?: string;
  created_by?: string;
  modified_on?: string | null;
  modified_by?: string | null;
  email?: string;
  [key: string]: unknown;
}

/** Login API response: token at root, user info in data. */
export interface LoginResponse {
  access_token?: string;
  token?: string;
  /** Used by POST /refresh — must not send access_token in its place. */
  refresh_token?: string;
  token_type?: string;
  data?: LoginUserData;
  [key: string]: unknown;
}

/** Values for the “Edit User Details” drawer, mapped from POST /api/v1/login stored payload. */
export interface EditProfileFormValues {
  status: 'Active' | 'Inactive';
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  org: string;
  title: string;
}

function pickStrFromUser(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return '';
  return pickScalarFromRecord(obj, keys);
}

const DEFAULT_LOGIN_FAILURE = 'Invalid username or password.';

/** Extract user-facing message from login HTTP error (401, 400, etc.). */
function parseLoginHttpError(err: unknown): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error;
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const msg = pickStringFromRecord(body as Record<string, unknown>, [
        'message',
        'detail',
        'error_description',
        'error',
        'title'
      ]);
      if (msg) return msg;
    }
    if (typeof body === 'string' && body.trim()) {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        const msg = pickStringFromRecord(parsed, ['message', 'detail', 'error_description', 'error']);
        if (msg) return msg;
      } catch {
        if (body.length < 400) return body.trim();
      }
    }
    if (err.status === 401 || err.status === 403) return DEFAULT_LOGIN_FAILURE;
  }
  return 'Login failed. Please check your credentials and try again.';
}

export type LoginOutcome = { ok: true } | { ok: false; errorMessage: string };

/** Returns JWT `exp` claim as milliseconds since epoch, or null if missing/invalid. */
function getJwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const exp = payload['exp'];
  const expSec =
    typeof exp === 'number' && Number.isFinite(exp)
      ? exp
      : typeof exp === 'string' && /^\d+$/.test(exp.trim())
        ? Number(exp.trim())
        : NaN;
  if (Number.isFinite(expSec)) return expSec * 1000;
  return null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly airportCodesStorage = inject(AirportCodesStorageService);
  private readonly overviewApi = inject(OverviewApiService);
  private readonly authLogger = inject(AuthLoggerService);
  private readonly msal = inject(MsalService, { optional: true });

  /** User_Role optionset — maps stored numeric `role` ids to Admin/Analyst for RBAC. */
  private readonly userRoleOptions = signal<OptionItem[]>([]);
  private readonly userProfileVersion = signal(0);
  private userRoleOptionsLoading = false;

  readonly isAuthenticated = signal<boolean>(this.readStoredAuth());

  constructor() {
    effect(() => {
      if (this.isAuthenticated()) {
        this.reloadUserRoleOptions();
      }
    });
  }

  /** Cache User_Role options (e.g. after User Management loads them). */
  setUserRoleOptions(opts: OptionItem[]): void {
    if (Array.isArray(opts) && opts.length > 0) {
      this.userRoleOptions.set(opts);
    }
  }

  private reloadUserRoleOptions(): void {
    if (this.userRoleOptionsLoading) return;
    this.userRoleOptionsLoading = true;
    this.overviewApi
      .getUserRoleOptions()
      .pipe(
        catchError(() => of([] as OptionItem[])),
        finalize(() => {
          this.userRoleOptionsLoading = false;
        })
      )
      .subscribe((opts) => this.userRoleOptions.set(opts));
  }

  /** Raw access role from session (`user_role` / `role`, string or numeric). */
  getAccessRoleRaw(): string {
    const u = this.getUserData() as Record<string, unknown> | null;
    if (!u) return '';
    return pickScalarFromRecord(u, [
      'user_role',
      'userRole',
      'UserRole',
      'access_role',
      'accessRole',
      'role',
      'Role',
      'title',
      'Title',
      'designation',
      'Designation'
    ]);
  }

  /** Resolve Admin/Analyst; optional options override session cache. */
  resolveAccessRole(options?: OptionItem[]): UserAccessRole | null {
    this.userProfileVersion();
    const opts = options ?? this.userRoleOptions();
    return resolveUserAccessRole(this.getAccessRoleRaw(), opts);
  }

  /**
   * Milliseconds since epoch when the current access JWT expires (`exp` claim), or null if missing/invalid.
   * Used to schedule proactive refresh while the user is active.
   */
  getAccessTokenExpiryMs(): number | null {
    const t = this.getToken();
    return t ? getJwtExpiryMs(t) : null;
  }

  /** Bearer token from login (for API requests). Always reads from localStorage so refreshed token is used. */
  getToken(): string | null {
    try {
      const raw = localStorage.getItem(TOKEN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /** Refresh token from login (for POST /refresh only). */
  getRefreshToken(): string | null {
    try {
      const raw = localStorage.getItem(REFRESH_TOKEN_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /** Persist token to localStorage. Call this whenever the token is updated (e.g. after refresh) so all requests use the new token. */
  setToken(token: string): void {
    if (token && typeof token === 'string') {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    }
  }

  /** Persist refresh token (login or token rotation from refresh response). */
  setRefreshToken(token: string): void {
    if (token && typeof token === 'string') {
      localStorage.setItem(REFRESH_TOKEN_KEY, JSON.stringify(token));
    }
  }

  /** Prevents duplicate logout when many requests fail refresh at once. */
  private refreshFailureLogoutIssued = false;

  private resetRefreshFailureGuard(): void {
    this.refreshFailureLogoutIssued = false;
  }

  /**
   * Single logout path when refresh cannot recover the session (HTTP error, missing tokens, etc.).
   */
  private logoutAfterRefreshFailure(reason: string): void {
    if (this.refreshFailureLogoutIssued) return;
    this.refreshFailureLogoutIssued = true;
    this.logout(reason);
  }

  /**
   * Identity for audit fields (`created_by`, `modified_by`). Prefer persisted login/user-details id;
   * fall back to profile blob, Entra JWT claims, then email when the API omits `user_id`.
   */
  getUserId(): string | null {
    try {
      const raw = localStorage.getItem(USER_ID_KEY);
      if (raw) {
        const v = JSON.parse(raw) as unknown;
        if (typeof v === 'string') {
          const t = v.trim();
          if (t) return t;
        }
      }
    } catch {
      /* fall through */
    }

    const u = this.getUserData() as Record<string, unknown> | null;
    if (u) {
      const fromBlob = pickStringFromRecord(u, [
        'user_id',
        'UserId',
        'userid',
        'USER_ID',
        'id',
        'Id'
      ]);
      if (fromBlob) return fromBlob;
    }

    const token = this.getToken();
    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload) {
        for (const k of ['oid', 'sub'] as const) {
          const v = payload[k];
          if (typeof v === 'string' && v.trim()) return v.trim();
        }
        const ni = payload['nameidentifier'];
        if (typeof ni === 'string' && ni.trim()) return ni.trim();
        const soapId = payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
        ];
        if (typeof soapId === 'string' && soapId.trim()) return soapId.trim();
      }
    }

    const email = this.getUserEmail().trim();
    return email || null;
  }

  /**
   * POST /api/v1/login with { username, password }.
   * On success stores token and auth; on failure returns { ok: false, errorMessage } (never errors the Observable).
   */
  login(username: string, password: string): Observable<LoginOutcome> {
    const url = `${environment.apiBaseUrl}/api/v1/login`;
    const body: LoginRequest = { username: username.trim(), password };

    return this.http.post<LoginResponse>(url, body, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    }).pipe(
      map((res): LoginOutcome => {
        const token = res?.access_token ?? res?.token ?? '';
        if (!token) {
          const resAny = (res ?? {}) as Record<string, unknown>;
          const msg =
            pickStringFromRecord(resAny, ['message', 'detail', 'error_description', 'error']) || DEFAULT_LOGIN_FAILURE;
          return { ok: false, errorMessage: msg };
        }
        this.persistLoginSuccess((res ?? {}) as Record<string, unknown>);
        this.authLogger.log('LOGIN_SUCCESS', { user: username.trim() });
        const expMs = getJwtExpiryMs(token);
        if (expMs != null) {
          this.authLogger.log('TOKEN_EXPIRY', { expiry: new Date(expMs).toISOString() });
        }
        return { ok: true };
      }),
      catchError((err): Observable<LoginOutcome> => of({ ok: false, errorMessage: parseLoginHttpError(err) }))
    );
  }

  private persistLoginSuccess(resAny: Record<string, unknown>): void {
    this.resetRefreshFailureGuard();
    const token = (resAny['access_token'] ?? resAny['token'] ?? '') as string;
    if (token) {
      localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    }
    let refreshToken = pickStringFromRecord(resAny, ['refresh_token', 'refreshToken']);
    const nestedData = resAny['data'];
    if (!refreshToken && nestedData && typeof nestedData === 'object' && !Array.isArray(nestedData)) {
      refreshToken = pickStringFromRecord(nestedData as Record<string, unknown>, ['refresh_token', 'refreshToken']);
    }
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, JSON.stringify(refreshToken));
    } else {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    this.applyUserProfileFromApiEnvelope(resAny);
    localStorage.setItem(AUTH_KEY, JSON.stringify({ ts: Date.now() }));
    this.isAuthenticated.set(true);
  }

  /**
   * Maps the same shape as POST /api/v1/login (`data` / nested `user` / root user fields) into
   * `spice.user_id` and `spice.user_data`. Used for login and for GET get_user_details (SSO).
   */
  private applyUserProfileFromApiEnvelope(resAny: Record<string, unknown>): void {
    const merged = mergeLoginPayloadToUserBlob(resAny);
    const userId = pickUserIdFromProfilePayload(merged, resAny);
    if (userId) {
      localStorage.setItem(USER_ID_KEY, JSON.stringify(userId));
    } else {
      localStorage.removeItem(USER_ID_KEY);
    }
    if (merged) {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
    } else {
      localStorage.removeItem(USER_DATA_KEY);
    }
    this.userProfileVersion.update((v) => v + 1);
    this.reloadUserRoleOptions();
  }

  /**
   * Persist `data[0]` from GET `/api/v1/user/get_user_details/:email` into session storage (`spice.user_*`).
   * Call only when that row is the **logged-in** user (e.g. header “Edit Profile”); not when editing another user from User Management.
   */
  refreshSessionFromGetUserDetailsResponse(res: GetUserDetailsResponse): void {
    const rows = res?.data;
    const row = Array.isArray(rows) && rows.length > 0 ? rows[0] : undefined;
    if (!row || typeof row !== 'object') return;
    const envelope: Record<string, unknown> = {
      success: res.success,
      message: res.message,
      data: row as Record<string, unknown>
    };
    this.applyUserProfileFromApiEnvelope(envelope);
  }

  /**
   * Email for GET /api/v1/user/get_user_details/:email (SSO). Prefer UPN; fall back to id_token claims.
   */
  private static azureAccountEmailForUserDetails(account: AccountInfo): string | null {
    const username = account.username?.trim() ?? '';
    if (username.includes('@')) return username;
    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const preferred = claims?.['preferred_username'];
    if (typeof preferred === 'string' && preferred.trim().includes('@')) return preferred.trim();
    const email = claims?.['email'];
    if (typeof email === 'string' && email.trim()) return email.trim();
    const upn = claims?.['upn'];
    if (typeof upn === 'string' && upn.trim().includes('@')) return upn.trim();
    return username || null;
  }

  /**
   * Call after a **finished** Microsoft redirect sign-in (`handleRedirect` returned an account).
   * Always GETs get_user_details — same moment we “have a token” for the new session.
   */
  fetchSsoUserDetailsAfterInteractiveLogin(): Observable<void> {
    if (!isAzureAdEnabled() || !this.msal) {
      return of(void 0);
    }
    const account = this.msal.instance.getActiveAccount();
    if (!account) {
      return of(void 0);
    }
    const email = AuthService.azureAccountEmailForUserDetails(account);
    if (!email) {
      this.authLogger.log('SSO_USER_DETAILS_SKIP', { reason: 'NO_EMAIL' });
      return of(void 0);
    }
    return this.getUserDetailsByEmail$(email, 'INTERACTIVE');
  }

  /**
   * Cold start with an **existing** MSAL session: keep `spice.user_id` / `spice.user_data` in localStorage
   * (like the token) and **do not** call the API if stored profile email matches the active account.
   * Fetches only when there is no usable stored profile (first visit, storage cleared, or different user).
   */
  ensureSsoUserDetailsFromSessionOrApi(): Observable<void> {
    if (!isAzureAdEnabled() || !this.msal) {
      return of(void 0);
    }
    const account = this.msal.instance.getActiveAccount();
    if (!account) {
      return of(void 0);
    }
    const email = AuthService.azureAccountEmailForUserDetails(account);
    if (!email) {
      this.authLogger.log('SSO_USER_DETAILS_SKIP', { reason: 'NO_EMAIL' });
      return of(void 0);
    }
    if (this.hasPersistedUserProfile() && this.storedSsoUserEmailMatchesAccount(account)) {
      this.authLogger.log('SSO_USER_DETAILS_USE_SESSION', { email });
      return of(void 0);
    }
    return this.getUserDetailsByEmail$(email, 'ENSURE');
  }

  private getUserDetailsByEmail$(
    email: string,
    source: 'INTERACTIVE' | 'ENSURE'
  ): Observable<void> {
    const url = `${environment.apiBaseUrl}/api/v1/user/get_user_details/${encodeURIComponent(email)}`;
    return this.http.get<Record<string, unknown>>(url, { headers: { Accept: 'application/json' } }).pipe(
      tap((res) => {
        this.applyUserProfileFromApiEnvelope(res);
        this.authLogger.log('SSO_USER_DETAILS_OK', { email, source });
      }),
      map(() => void 0),
      catchError((err: unknown) => {
        const status = err instanceof HttpErrorResponse ? err.status : undefined;
        this.authLogger.log('SSO_USER_DETAILS_FAIL', { email, source, status });
        return of(void 0);
      })
    );
  }

  private hasPersistedUserProfile(): boolean {
    try {
      const id = localStorage.getItem(USER_ID_KEY);
      const data = localStorage.getItem(USER_DATA_KEY);
      if (id && data) return true;
      if (data) {
        const p = JSON.parse(data) as Record<string, unknown>;
        return p != null && typeof p === 'object' && Object.keys(p).length > 0;
      }
    } catch {
      /* ignore */
    }
    return false;
  }

  /** True when `spice.user_data` email matches the Entra account (so we can skip get_user_details on reload). */
  private storedSsoUserEmailMatchesAccount(account: AccountInfo): boolean {
    const acc = AuthService.azureAccountEmailForUserDetails(account);
    if (!acc) return false;
    const u = this.getUserData() as Record<string, unknown> | null;
    if (!u) return false;
    const fromStored = pickStrFromUser(u, [
      'email',
      'Email',
      'mail',
      'Mail',
      'user_principal_name',
      'userPrincipalName',
      'upn',
      'preferred_username',
      'preferredUsername'
    ]);
    if (!fromStored.trim()) return false;
    return fromStored.trim().toLowerCase() === acc.trim().toLowerCase();
  }

  /** User data from login response (data object). */
  getUserData(): LoginUserData | null {
    try {
      const raw = localStorage.getItem(USER_DATA_KEY);
      return raw ? (JSON.parse(raw) as LoginUserData) : null;
    } catch {
      return null;
    }
  }

  /** Preferred identity string for audit fields that need email/UPN. */
  getUserEmail(): string {
    const u = this.getUserData() as Record<string, unknown> | null;
    const fromStored = pickStrFromUser(u, [
      'email',
      'Email',
      'mail',
      'Mail',
      'user_principal_name',
      'userPrincipalName',
      'upn',
      'preferred_username',
      'preferredUsername'
    ]);
    if (fromStored) return fromStored;
    if (isAzureAdEnabled()) {
      const a = this.msal?.instance.getActiveAccount();
      const fromAccount = a?.username?.trim() ?? '';
      if (fromAccount) return fromAccount;
      const claims = a?.idTokenClaims as Record<string, unknown> | undefined;
      const preferred = claims?.['preferred_username'];
      if (typeof preferred === 'string' && preferred.trim()) return preferred.trim();
      const email = claims?.['email'];
      if (typeof email === 'string' && email.trim()) return email.trim();
      const upn = claims?.['upn'];
      if (typeof upn === 'string' && upn.trim()) return upn.trim();
    }
    return '';
  }

  /**
   * Map stored login payload (localStorage) to Edit Profile form defaults.
   * Supports common API field names (snake_case / PascalCase).
   */
  getEditProfileFormValues(): EditProfileFormValues {
    const u = this.getUserData() as Record<string, unknown> | null;
    const empty: EditProfileFormValues = {
      status: 'Active',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      org: '',
      title: ''
    };
    if (!u) return empty;

    let firstName = pickStrFromUser(u, ['first_name', 'FirstName', 'given_name', 'GivenName']);
    let lastName = pickStrFromUser(u, ['last_name', 'LastName', 'family_name', 'FamilyName']);
    if (!firstName && !lastName) {
      const full = pickStrFromUser(u, [
        'full_name',
        'FullName',
        'name',
        'Name',
        'user_name',
        'username',
        'display_name',
        'DisplayName'
      ]);
      const parts = full.trim().split(/\s+/).filter(Boolean);
      firstName = parts[0] ?? '';
      lastName = parts.slice(1).join(' ');
    }

    const isActive = u['is_active'];
    const status: 'Active' | 'Inactive' =
      isActive === false || pickStrFromUser(u, ['status', 'Status']).toLowerCase() === 'inactive'
        ? 'Inactive'
        : 'Active';

    return {
      status,
      firstName,
      lastName,
      email: pickStrFromUser(u, ['email', 'Email', 'mail', 'Mail']),
      phone: pickStrFromUser(u, ['business_phone', 'phone', 'Phone', 'mobile', 'telephone', 'MobilePhone']),
      org: pickStrFromUser(u, ['organization', 'organization_name', 'OrganizationName', 'org_name', 'company', 'CompanyName']),
      /** Title field follows login “role” first (same sources as {@link getUserRoleDisplay}), then job title / designation. */
      title: pickStrFromUser(u, [
        'role',
        'Role',
        'user_role',
        'title',
        'Title',
        'job_title',
        'JobTitle',
        'designation',
        'Designation'
      ])
    };
  }

  /**
   * Recompute `isAuthenticated` when Entra ID is enabled (active MSAL account or legacy SPICE session).
   * Called after redirect handling and MSAL broadcast events.
   */
  syncAzureSessionState(): void {
    if (!isAzureAdEnabled()) {
      return;
    }
    const entra = Boolean(this.msal?.instance.getActiveAccount());
    const legacy = this.readStoredAuth();
    this.isAuthenticated.set(entra || legacy);
  }

  /** Display name for header: supports user_name, username, name, full_name, email. */
  getUserDisplayName(): string {
    if (isAzureAdEnabled()) {
      const a = this.msal?.instance.getActiveAccount();
      if (a) {
        const name = a.name?.trim();
        if (name) return name;
        const username = a.username?.trim();
        if (username) return username;
        const claims = a.idTokenClaims as Record<string, unknown> | undefined;
        const preferred = claims?.['preferred_username'];
        if (typeof preferred === 'string' && preferred.trim()) return preferred.trim();
      }
    }
    const u = this.getUserData() as Record<string, unknown> | null;
    if (!u) return '';
    return pickStringFromRecord(u, [
      'user_name',
      'username',
      'name',
      'full_name',
      'display_name',
      'email'
    ]);
  }

  /** Role line for header: supports role, user_role, designation. */
  getUserRoleDisplay(): string {
    const u = this.getUserData() as Record<string, unknown> | null;
    if (!u) return '';
    return pickStringFromRecord(u, ['role', 'Role', 'user_role', 'designation']);
  }

  /**
   * Logged-in user's access role (Admin / Analyst).
   * Resolves text roles and numeric User_Role optionset ids from session `user_role` / `role`.
   */
  getUserAccessRole(): UserAccessRole | null {
    return this.resolveAccessRole();
  }

  isAdmin(): boolean {
    return this.resolveAccessRole() === 'admin';
  }

  isAnalyst(): boolean {
    return this.resolveAccessRole() === 'analyst';
  }

  /** True when the row matches the signed-in user (email or user id). */
  isSameUserAsSession(email: string | null | undefined, userId?: string | null | undefined): boolean {
    const rowEmail = String(email ?? '').trim().toLowerCase();
    const meEmail = this.getUserEmail().trim().toLowerCase();
    if (rowEmail && meEmail && rowEmail === meEmail) return true;
    const rowId = String(userId ?? '').trim();
    const meId = this.getUserId()?.trim() ?? '';
    return !!(rowId && meId && rowId === meId);
  }

  /** Refresh endpoint path; interceptor skips Bearer and 401 retry when URL is this path. */
  getRefreshPath(): string {
    return getRefreshPath();
  }

  /**
   * Single in-flight refresh observable. Concurrent 401s share this stream (see shareReplay + finalize).
   */
  private refreshInFlight: Observable<string | null> | null = null;

  /**
   * Exchange refresh_token for a new access token. Sends `{ refresh_token }` in the body (not the access token).
   * Updates localStorage via setToken / setRefreshToken when the API returns new tokens.
   * Only one HTTP refresh runs at a time; concurrent callers share the same result.
   */
  refreshToken(): Observable<string | null> {
    const refreshTokenValue = this.getRefreshToken();
    if (!refreshTokenValue) {
      this.authLogger.log('REFRESH_FAILED', { reason: 'NO_REFRESH_TOKEN_STORED' });
      this.logoutAfterRefreshFailure('REFRESH_NO_REFRESH_TOKEN');
      return of(null);
    }
    if (!this.refreshInFlight) {
      this.authLogger.log('REFRESH_START');
      const url = `${environment.apiBaseUrl}${getRefreshPath()}`;
      this.refreshInFlight = this.http
        .post<Record<string, unknown>>(url, { refresh_token: refreshTokenValue }, {
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' }
        })
        .pipe(
          map((res) => {
            let rec = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
            const nested = rec['data'];
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
              rec = { ...rec, ...(nested as Record<string, unknown>) };
            }
            const newRefresh = pickStringFromRecord(rec, ['refresh_token', 'refreshToken']);
            if (newRefresh) {
              this.setRefreshToken(newRefresh);
            }
            const newTokenRaw = pickStringFromRecord(rec, ['access_token', 'token']);
            const t = newTokenRaw.trim() ? newTokenRaw.trim() : null;
            if (t) {
              this.setToken(t);
              this.authLogger.log('REFRESH_SUCCESS');
              const expMs = getJwtExpiryMs(t);
              if (expMs != null) {
                this.authLogger.log('TOKEN_EXPIRY', { expiry: new Date(expMs).toISOString(), source: 'refresh' });
              }
              return t;
            }
            this.authLogger.log('REFRESH_FAILED', { reason: 'NO_TOKEN_IN_RESPONSE' });
            this.logoutAfterRefreshFailure('REFRESH_NO_TOKEN_IN_RESPONSE');
            return null;
          }),
          catchError((err) => {
            this.authLogger.log('REFRESH_FAILED', {
              reason: 'HTTP_ERROR',
              status: err instanceof HttpErrorResponse ? err.status : undefined
            });
            this.logoutAfterRefreshFailure('REFRESH_HTTP_ERROR');
            return of(null);
          }),
          shareReplay(1),
          finalize(() => {
            this.refreshInFlight = null;
          })
        );
    }
    return this.refreshInFlight;
  }

  /**
   * Clear session. Optional `reason` is written to persistent auth logs before storage is cleared.
   * @returns `true` when Entra `logoutRedirect` was started (browser will navigate away; skip extra redirects).
   */
  logout(reason?: string): boolean {
    const willMsalRedirect = isAzureAdEnabled() && Boolean(this.msal?.instance.getActiveAccount());
    this.authLogger.log('LOGOUT', { reason: reason ?? 'UNKNOWN' });
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    this.airportCodesStorage.clear();
    this.isAuthenticated.set(false);
    if (willMsalRedirect && this.msal) {
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      void this.msal.instance.logoutRedirect({
        postLogoutRedirectUri: origin ? `${origin}/login` : undefined
      });
    }
    return willMsalRedirect;
  }

  private readStoredAuth(): boolean {
    try {
      return Boolean(localStorage.getItem(AUTH_KEY));
    } catch {
      return false;
    }
  }
}
